import React, { useState, useRef, useEffect, useCallback } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { 
  Dialog, 
  Box, 
  Typography, 
  IconButton,
  Paper,
  Stack,
  Divider,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
// Import images
import adhaarMask from '../../Assets/img/Kyc/adhaarMask.png';
import Lion from '../../Assets/img/Kyc/Lion.png';
import Flag from '../../Assets/img/Kyc/Flag.png';
import digi from '../../Assets/img/Kyc/digi2.jpg';
import logo from '../../Assets/img/default-logo.png';

const AadhaarCard = ({ digiInfo, qrCodeUrl, websiteUrl, docType = "aadhar", token, forceFlip }) => {
    const [isCardVisible, setIsCardVisible] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const qrRef = useRef(null);
    const modalQrRef = useRef(null);
    const [qrCode, setQrCode] = useState(null);
    const [modalQrCode, setModalQrCode] = useState(null);

    const handleImageClick = () => {
        setIsCardVisible(!isCardVisible);
    };

    const handleQrClick = (e) => {
        e.stopPropagation();
        setShowQrModal(true);
    };

    const handleCloseModal = () => {
        setShowQrModal(false);
    };

    const getPublicDocViewUrl = () => {
        const authToken = token || localStorage.getItem("objectId");
        return `${window.location.origin}/public-docview?token=${encodeURIComponent(authToken)}&docType=${docType}`;
    };

    useEffect(() => {
        const qrUrl = getPublicDocViewUrl();
        if (qrUrl) {
            const qrCodeInstance = new QRCodeStyling({
                width: 120,
                height: 120,
                type: "canvas",
                data: qrUrl,
                image: logo,
                margin: 5,
                qrOptions: {
                    typeNumber: "0",
                    mode: "Byte",
                    errorCorrectionLevel: "H"
                },
                imageOptions: {
                    hideBackgroundDots: true,
                    imageSize: 0.4,
                    margin: 5,
                    crossOrigin: "anonymous",
                },
                dotsOptions: {
                    color: "#000000",
                    type: "rounded"
                },
                backgroundOptions: {
                    color: "#ffffff",
                },
                cornersSquareOptions: {
                    type: "extra-rounded",
                    color: "#000000"
                },
                cornersDotOptions: {
                    type: "dot",
                    color: "#000000"
                }
            });

            setQrCode(qrCodeInstance);
        }
    }, [qrCodeUrl, token, docType]);

    useEffect(() => {
        if (showQrModal) {
            const qrUrl = getPublicDocViewUrl();
            const modalQrInstance = new QRCodeStyling({
                width: 400,
                height: 400,
                type: "canvas",
                data: qrUrl,
                image: logo,
                margin: 10,
                qrOptions: {
                    typeNumber: "0",
                    mode: "Byte",
                    errorCorrectionLevel: "H"
                },
                imageOptions: {
                    hideBackgroundDots: true,
                    imageSize: 0.3,
                    margin: 10,
                    crossOrigin: "anonymous",
                },
                dotsOptions: {
                    color: "#000000",
                    type: "rounded"
                },
                backgroundOptions: {
                    color: "#ffffff",
                },
                cornersSquareOptions: {
                    type: "extra-rounded",
                    color: "#000000"
                },
                cornersDotOptions: {
                    type: "dot",
                    color: "#000000"
                }
            });

            setModalQrCode(modalQrInstance);
        }
    }, [showQrModal, qrCodeUrl, token, docType]);

    const qrRefCallback = useCallback((node) => {
        if (node && qrCode) {
            node.innerHTML = '';
            qrCode.append(node);
        }
    }, [qrCode]);

    const modalQrRefCallback = useCallback((node) => {
        if (node && modalQrCode) {
            node.innerHTML = '';
            modalQrCode.append(node);
        }
    }, [modalQrCode]);

    const publicUrl = getPublicDocViewUrl();

    return (
        <>
            <Box
                onClick={handleImageClick}
                sx={{
                    width: "400px",
                    height: "590px",
                    margin: "auto",
                    perspective: "1000px",
                    cursor: "pointer",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        transformStyle: "preserve-3d",
                        transition: "transform 0.6s",
                        transform: (isCardVisible || forceFlip) ? "rotateY(180deg)" : "rotateY(0deg)",
                    }}
                >
                    {/* Front Side */}
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            backfaceVisibility: "hidden",
                            position: "absolute",
                            top: 0,
                            left: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={adhaarMask}
                            alt="Aadhaar Mask"
                            sx={{
                                width: "100%",
                                height: "100%",
                                border: "2px solid gray",
                                borderRadius: "15px",
                            }}
                        />
                    </Box>

                    {/* Back Side */}
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            backfaceVisibility: "hidden",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            transform: "rotateY(180deg)",
                            backgroundColor: "white",
                            border: "2px solid gray",
                            borderRadius: "15px",
                            padding: "10px",
                            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                            overflow: "hidden",
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-evenly"
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ rowGap: "10px" }}
                        >
                            <Box
                                component="img"
                                src={Lion}
                                alt="Lion Logo"
                                sx={{
                                    width: "15%",
                                    minWidth: "50px",
                                    objectFit: "contain"
                                }}
                            />
                            <Box
                                component="img"
                                src={Flag}
                                alt="Flag Logo"
                                sx={{
                                    width: "20%",
                                    minWidth: "50px",
                                    objectFit: "contain"
                                }}
                            />
                            <Box
                                component="img"
                                src="https://www.pngkey.com/png/full/224-2240746_aadhaar-logo-aadhar-card-logo-png.png"
                                alt="Aadhaar Logo"
                                sx={{
                                    width: "20%",
                                    minWidth: "50px",
                                    objectFit: "contain"
                                }}
                            />
                        </Stack>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            sx={{
                                padding: "10px",
                                alignItems: "center",
                                flexWrap: "wrap",
                                width: "100%",
                            }}
                        >
                            <Box
                                component="img"
                                src={
                                    digiInfo?.photo
                                        ? `data:image/png;base64,${digiInfo?.photo}`
                                        : "https://icon-library.com/images/man-png-icon/man-png-icon-14.jpg"
                                }
                                alt="Profile"
                                sx={{
                                    width: "30%",
                                    minWidth: "120px",
                                }}
                            />
                            <Box
                                sx={{
                                    textAlign: "left",
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    minWidth: "200px",
                                    padding: "10px",
                                }}
                            >
                                <Typography variant="body2" component="div">
                                    <strong>Name:</strong> {digiInfo?.name || "N/A"}
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <strong>Aadhaar:</strong> {digiInfo?.masked_id || "N/A"}
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <strong>DOB:</strong> {digiInfo?.dob || "N/A"}
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <strong>Gender:</strong> {digiInfo?.gender || "N/A"}
                                </Typography>
                            </Box>
                        </Stack>

                        <Divider />

                        <Box sx={{ textAlign: "left", padding: "10px" }}>
                            <Typography variant="body2">
                                <strong>Address:</strong> {digiInfo?.house || ""} {digiInfo?.street || ""}{" "}
                                {digiInfo?.locality || ""} {digiInfo?.city || ""}{" "}
                                {digiInfo?.state || ""}
                            </Typography>
                        </Box>

                        <Divider />

                        <Stack
                            direction="row"
                            justifyContent="space-evenly"
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{
                                padding: "20px",
                                rowGap: "15px",
                            }}
                        >
                            <Box
                                component="img"
                                src={digi}
                                alt="Digi Info"
                                sx={{
                                    width: "55%",
                                    minWidth: "100px",
                                    height: "auto",
                                    objectFit: "contain",
                                }}
                            />

                            <Box
                                onClick={handleQrClick}
                                sx={{
                                    width: "25%",
                                    minWidth: "100px",
                                    position: "relative",
                                    cursor: "pointer",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                }}
                            >
                                {qrCodeUrl || publicUrl ? (
                                    <>
                                        <Box
                                            ref={qrRefCallback}
                                            sx={{
                                                width: "100px",
                                                height: "100px",
                                                margin: "0 auto",
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontSize: "8px",
                                                color: "#666",
                                                marginTop: "5px",
                                                textAlign: "center",
                                            }}
                                        >
                                            Tap to scan
                                        </Typography>
                                    </>
                                ) : (
                                    <Box
                                        component="img"
                                        src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://neoin.com"
                                        alt="QR Code"
                                        sx={{
                                            width: "100%",
                                            height: "auto",
                                            objectFit: "contain",
                                        }}
                                    />
                                )}
                            </Box>
                        </Stack>

                        <Divider sx={{ width: "80%", margin: "auto", padding: "5px", borderColor: "red" }} />

                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2">
                                <span style={{ color: "red" }}>आधार</span> - आम आदमी का अधिकार
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* QR Code Modal */}
            <Dialog 
                open={showQrModal} 
                onClose={handleCloseModal} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        backgroundColor: "white",
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "30px",
                        backgroundColor: "white",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: "gray",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography 
                        variant="h6" 
                        sx={{ 
                            mb: 3, 
                            textAlign: "center",
                            fontWeight: 600,
                            color: "#1e293b"
                        }}
                    >
                        Scan QR Code to View Document
                    </Typography>

                    <Typography 
                        variant="body2" 
                        sx={{ 
                            mb: 3, 
                            textAlign: "center",
                            color: "#64748b"
                        }}
                    >
                        Use Digilocker or any QR scanner app
                    </Typography>

                    <Box
                        ref={modalQrRefCallback}
                        onClick={() => {
                            const publicUrl = getPublicDocViewUrl();
                            if (publicUrl) {
                                window.open(publicUrl, '_blank');
                            }
                        }}
                        sx={{
                            width: "350px",
                            height: "350px",
                            margin: "0 auto",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                        }}
                    />
                </Box>
            </Dialog>
        </>
    );
};

export default AadhaarCard;